import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { selectFilters } from 'shared/selectors';
import { EDealType, EOfferType } from 'shared/types/features';
import {
  caClickLinksQuestions,
  caPageCategory,
  openArticle,
  openQuestion,
  prepareCategoryCa,
} from 'shared/utils/tracking';

export type TUseGuideTrackingResult = {
  category: string;
  trackPageCategory: (nextDealType?: EDealType | null, nextOfferType?: EOfferType | null) => void;
  trackArticleClick: (articleName: string, articleLink: string) => void;
  trackFaqLinkClick: (questionId: number, question: string, link: string) => void;
  trackFaqQuestionOpen: (questionId: number, question: string) => void;
};

export const useGuideTracking = (): TUseGuideTrackingResult => {
  const { dealType, offerType } = useSelector(selectFilters);
  const category = prepareCategoryCa(dealType, offerType);

  const trackPageCategory = useCallback(
    (nextDealType?: EDealType | null, nextOfferType?: EOfferType | null): void => {
      const resolvedDealType = nextDealType !== undefined ? nextDealType : dealType;
      const resolvedOfferType = nextOfferType !== undefined ? nextOfferType : offerType;

      caPageCategory(prepareCategoryCa(resolvedDealType, resolvedOfferType));
    },
    [dealType, offerType],
  );

  const trackArticleClick = useCallback(
    (articleName: string, articleLink: string): void => {
      openArticle(articleName, articleLink, category);
    },
    [category],
  );

  const trackFaqLinkClick = useCallback(
    (questionId: number, question: string, link: string): void => {
      caClickLinksQuestions(category, questionId, question, link);
    },
    [category],
  );

  const trackFaqQuestionOpen = useCallback(
    (questionId: number, question: string): void => {
      openQuestion(question, questionId, category);
    },
    [category],
  );

  return {
    category,
    trackPageCategory,
    trackArticleClick,
    trackFaqLinkClick,
    trackFaqQuestionOpen,
  };
};
